import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { BankSearch } from '../../contas/components/BankSearch';
import { CurrencyInput } from '../../../components/base/CurrencyInput';
import type { Card, CardBrand, CardCurrency, CardPaymentMethod, CardType } from '../../../hooks/useCards';

interface LinkedAccount {
  id: string;
  name: string;
  bank_name: string;
  type: string;
  color: string;
}

interface CardModalProps {
  open: boolean;
  card?: Card | null;
  onClose: () => void;
  onSave: (values: Omit<Card, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<{ error: string | null }>;
}

interface BrandOption {
  value: CardBrand;
  label: string;
  shortLabel: string;
  bg: string;
  textColor: string;
  icon: string;
}

const BRANDS: BrandOption[] = [
  { value: 'visa', label: 'Visa', shortLabel: 'VISA', bg: 'bg-[#1a1f71]', textColor: 'text-white', icon: 'ri-visa-line' },
  { value: 'mastercard', label: 'Mastercard', shortLabel: 'MC', bg: 'bg-gradient-to-r from-[#eb001b] to-[#f79e1b]', textColor: 'text-white', icon: 'ri-mastercard-line' },
  { value: 'elo', label: 'Elo', shortLabel: 'ELO', bg: 'bg-[#ffcb05]', textColor: 'text-gray-900', icon: 'ri-bank-card-line' },
  { value: 'amex', label: 'Amex', shortLabel: 'AMEX', bg: 'bg-[#007bc1]', textColor: 'text-white', icon: 'ri-bank-card-2-line' },
  { value: 'hipercard', label: 'Hipercard', shortLabel: 'HIPER', bg: 'bg-[#b3131b]', textColor: 'text-white', icon: 'ri-bank-card-line' },
  { value: 'outro', label: 'Outra', shortLabel: 'CARD', bg: 'bg-gray-200', textColor: 'text-gray-600', icon: 'ri-bank-card-line' },
];

const COLORS = [
  '#1e293b', '#7c3aed', '#0f172a', '#1d4ed8',
  '#059669', '#dc2626', '#d97706', '#0891b2',
  '#be185d', '#4338ca', '#047857', '#374151',
];

const CURRENCIES: { value: CardCurrency; label: string }[] = [
  { value: 'BRL', label: 'Real (BRL)' },
  { value: 'USD', label: 'Dólar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
];

const PAYMENT_METHODS: { value: CardPaymentMethod; label: string; description: string; icon: string }[] = [
  { value: 'manual', label: 'Manual', description: 'Você paga a fatura manualmente', icon: 'ri-hand-coin-line' },
  { value: 'automatic_debit', label: 'Débito automático', description: 'Debita automaticamente da conta vinculada', icon: 'ri-loop-right-line' },
  { value: 'external', label: 'Pagamento externo', description: 'Pago via outro meio (app, banco etc.)', icon: 'ri-external-link-line' },
];

const CARD_TYPES: { value: CardType; label: string; icon: string; desc: string }[] = [
  { value: 'credit', label: 'Crédito', icon: 'ri-bank-card-2-line', desc: 'Fatura mensal, limite de crédito, parcelamento' },
  { value: 'debit', label: 'Débito', icon: 'ri-bank-card-line', desc: 'Débita direto da conta, sem fatura' },
  { value: 'multiple', label: 'Múltiplo', icon: 'ri-swap-line', desc: 'Funciona como crédito e débito' },
];

const defaultForm = (): Omit<Card, 'id' | 'user_id' | 'created_at' | 'updated_at'> => ({
  name: '', nickname: '', bank: '', bank_name: '', bank_code: '',
  brand: 'outro', last_four_digits: '', limit_amount: 0, currency: 'BRL',
  closing_day: 10, due_day: 20, color: '#1e293b',
  is_active: true, is_archived: false, notes: '',
  linked_account_id: null, payment_method: 'manual',
  card_type: 'credit',
});

export const CardModal = ({ open, card, onClose, onSave }: CardModalProps) => {
  const { user } = useAuth();
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);

  useEffect(() => {
    if (!user || !open) return;
    supabase
      .from('accounts')
      .select('id, name, bank_name, type, color')
      .eq('user_id', user.id)
      .eq('is_archived', false)
      .order('name')
      .then(({ data }) => setLinkedAccounts((data ?? []) as LinkedAccount[]));
  }, [user, open]);

  useEffect(() => {
    if (open) {
      if (card) {
        setForm({
          name: card.name ?? '',
          nickname: card.nickname ?? '',
          bank: card.bank ?? '',
          bank_name: card.bank_name ?? '',
          bank_code: card.bank_code ?? '',
          brand: card.brand ?? 'outro',
          last_four_digits: card.last_four_digits ?? '',
          limit_amount: card.limit_amount ?? 0,
          currency: card.currency ?? 'BRL',
          closing_day: card.closing_day ?? 10,
          due_day: card.due_day ?? 20,
          color: card.color ?? '#1e293b',
          is_active: card.is_active ?? true,
          is_archived: card.is_archived ?? false,
          notes: card.notes ?? '',
          linked_account_id: card.linked_account_id ?? null,
          payment_method: card.payment_method ?? 'manual',
          card_type: card.card_type ?? 'credit',
        });
      } else {
        setForm(defaultForm());
      }
      setErrorMsg('');
    }
  }, [open, card]);

  const isDebitOrMultiple = form.card_type === 'debit' || form.card_type === 'multiple';
  const isCreditOnly = form.card_type === 'credit';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    // Validate: debit/multiple requires linked account
    if (isDebitOrMultiple && !form.linked_account_id) {
      setErrorMsg(
        form.card_type === 'debit'
          ? 'Cartão de débito requer uma conta vinculada para débito direto.'
          : 'Cartão múltiplo requer uma conta vinculada para a função débito.'
      );
      return;
    }

    if (isCreditOnly) {
      if (form.closing_day < 1 || form.closing_day > 28) {
        setErrorMsg('Dia de fechamento deve ser entre 1 e 28.');
        return;
      }
      if (form.due_day < 1 || form.due_day > 28) {
        setErrorMsg('Dia de vencimento deve ser entre 1 e 28.');
        return;
      }
    }

    // For pure debit: clear credit-specific fields
    const finalForm = { ...form, bank_name: form.bank };
    if (form.card_type === 'debit') {
      finalForm.limit_amount = 0;
    }

    setSaving(true);
    const result = await onSave(finalForm);
    setSaving(false);
    if (result?.error) setErrorMsg('Erro ao salvar. Tente novamente.');
    else onClose();
  };

  const previewDigits = form.last_four_digits ? `•••• ${form.last_four_digits}` : '•••• ••••';
  const selectedBrand = BRANDS.find(b => b.value === form.brand);
  const linkedAccount = linkedAccounts.find(a => a.id === form.linked_account_id);
  const selectedCardType = CARD_TYPES.find(t => t.value === form.card_type);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-base font-700 text-gray-900">{card ? 'Editar Cartão' : 'Novo Cartão'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 overflow-y-auto flex-1">

          {/* Mini preview */}
          <div
            className="h-16 rounded-xl flex items-center justify-between px-4 relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${form.color}, ${form.color}cc)` }}
          >
            <div className="absolute top-0 right-0 w-20 h-20 rounded-full opacity-10 bg-white" style={{ transform: 'translate(30%,-30%)' }} />
            <div>
              <p className="text-white/70 text-xs">{form.bank || 'Emissor'}</p>
              <p className="text-white text-sm font-700">{form.name || 'Nome do cartão'}</p>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-white/80 text-xs font-mono">{previewDigits}</p>
              <div className="flex flex-col items-end gap-0.5">
                <span className={`text-xs font-800 px-2 py-0.5 rounded-md opacity-90 ${selectedBrand?.bg ?? 'bg-gray-200'} ${selectedBrand?.textColor ?? 'text-gray-600'}`}>
                  {selectedBrand?.shortLabel ?? 'CARD'}
                </span>
                <span className="text-white/60 text-[9px] font-500 uppercase tracking-wide">{selectedCardType?.label}</span>
              </div>
            </div>
          </div>

          {/* ── Tipo do cartão ── */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              Tipo do cartão *
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CARD_TYPES.map(ct => (
                <button
                  key={ct.value}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, card_type: ct.value }))}
                  className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                    form.card_type === ct.value
                      ? 'border-emerald-400 bg-emerald-50'
                      : 'border-gray-150 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${
                    form.card_type === ct.value ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    <i className={`${ct.icon} text-sm ${form.card_type === ct.value ? 'text-emerald-600' : 'text-gray-500'}`} />
                  </div>
                  <span className={`text-xs font-700 ${form.card_type === ct.value ? 'text-emerald-700' : 'text-gray-600'}`}>
                    {ct.label}
                  </span>
                </button>
              ))}
            </div>
            {selectedCardType && (
              <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                <i className="ri-information-line text-gray-300" />
                {selectedCardType.desc}
              </p>
            )}
          </div>

          {/* Nome + apelido */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Nome do cartão *</label>
              <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Ex: Nubank, Itaú Gold..."
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
                Apelido <span className="normal-case text-gray-400 font-400">(opcional)</span>
              </label>
              <input value={form.nickname} onChange={e => setForm(p => ({ ...p, nickname: e.target.value }))}
                placeholder="Ex: Cartão do trabalho"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>
          </div>

          {/* Banco com autocomplete */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Banco / Emissor</label>
            <BankSearch
              value={form.bank}
              bankCode={form.bank_code}
              onChange={(name, code) => setForm(p => ({ ...p, bank: name, bank_name: name, bank_code: code }))}
              placeholder="Buscar banco ou código..."
              icon="ri-bank-line"
              codeLabel="Cód."
            />
          </div>

          {/* Bandeira */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Bandeira</label>
            <div className="grid grid-cols-3 gap-2">
              {BRANDS.map(b => (
                <button
                  key={b.value}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, brand: b.value }))}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                    form.brand === b.value
                      ? 'border-emerald-400 bg-emerald-50'
                      : 'border-gray-150 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`w-8 h-5 rounded flex items-center justify-center flex-shrink-0 ${b.bg}`}>
                    <span className={`text-xs font-800 leading-none ${b.textColor}`} style={{ fontSize: '9px', letterSpacing: '0.02em' }}>
                      {b.shortLabel}
                    </span>
                  </div>
                  <span className={`text-sm font-600 truncate ${form.brand === b.value ? 'text-emerald-700' : 'text-gray-700'}`}>
                    {b.label}
                  </span>
                  {form.brand === b.value && (
                    <i className="ri-check-line text-emerald-500 text-xs ml-auto flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 4 últimos dígitos */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              4 últimos dígitos <span className="normal-case text-gray-400 font-400">(opcional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-mono">••••</span>
              <input
                value={form.last_four_digits}
                onChange={e => setForm(p => ({ ...p, last_four_digits: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                placeholder="1234"
                maxLength={4}
                inputMode="numeric"
                className="w-full pl-14 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Apenas para identificação visual. Nunca pedimos número completo, CVV ou validade.</p>
          </div>

          {/* Limite + Moeda — somente para crédito/múltiplo */}
          {form.card_type !== 'debit' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Limite total</label>
                <CurrencyInput
                  value={form.limit_amount}
                  onChange={(v) => setForm(p => ({ ...p, limit_amount: v }))}
                  placeholder="0,00"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
                />
              </div>
              <div>
                <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Moeda</label>
                <select
                  value={form.currency}
                  onChange={e => setForm(p => ({ ...p, currency: e.target.value as CardCurrency }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-white"
                >
                  {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Moeda apenas (para débito) */}
          {form.card_type === 'debit' && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Moeda</label>
              <select
                value={form.currency}
                onChange={e => setForm(p => ({ ...p, currency: e.target.value as CardCurrency }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-white"
              >
                {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          )}

          {/* Fechamento + Vencimento — somente para crédito/múltiplo */}
          {form.card_type !== 'debit' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Dia de fechamento</label>
                <input
                  type="number" min={1} max={28} value={form.closing_day}
                  onChange={e => setForm(p => ({ ...p, closing_day: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
                />
                <p className="text-xs text-gray-400 mt-1">Dia em que a fatura fecha</p>
              </div>
              <div>
                <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Dia de vencimento</label>
                <input
                  type="number" min={1} max={28} value={form.due_day}
                  onChange={e => setForm(p => ({ ...p, due_day: parseInt(e.target.value) || 10 }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
                />
                <p className="text-xs text-gray-400 mt-1">Prazo para pagar a fatura</p>
              </div>
            </div>
          )}

          {/* Conta vinculada */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              {form.card_type === 'debit'
                ? 'Conta para débito *'
                : form.card_type === 'multiple'
                ? 'Conta vinculada (débito) *'
                : <span>Conta para pagar a fatura <span className="normal-case text-gray-400 font-400">(opcional)</span></span>}
            </label>
            <select
              value={form.linked_account_id ?? ''}
              onChange={e => setForm(p => ({ ...p, linked_account_id: e.target.value || null }))}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-white ${
                isDebitOrMultiple && !form.linked_account_id ? 'border-amber-300' : 'border-gray-200'
              }`}
            >
              <option value="">
                {isDebitOrMultiple ? 'Selecione uma conta...' : 'Nenhuma conta vinculada'}
              </option>
              {linkedAccounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.name}{acc.bank_name ? ` — ${acc.bank_name}` : ''}
                </option>
              ))}
            </select>
            {linkedAccount && (
              <div className="mt-1.5 flex items-center gap-2 px-2.5 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: linkedAccount.color || '#10b981' }} />
                <p className="text-xs text-emerald-700 font-500">
                  {form.card_type === 'debit'
                    ? <>Débitos sairão diretamente de <strong>{linkedAccount.name}</strong></>
                    : form.card_type === 'multiple'
                    ? <>Quando usado como débito, sairá de <strong>{linkedAccount.name}</strong></>
                    : <>Pagamentos da fatura virão de <strong>{linkedAccount.name}</strong></>
                  }
                </p>
              </div>
            )}
            {isDebitOrMultiple && !form.linked_account_id && (
              <div className="mt-1.5 flex items-center gap-2 px-2.5 py-1.5 bg-amber-50 rounded-lg border border-amber-100">
                <i className="ri-alert-line text-amber-500 text-xs" />
                <p className="text-xs text-amber-700">
                  {form.card_type === 'debit'
                    ? 'Obrigatório: cartão de débito precisa de uma conta para debitar.'
                    : 'Obrigatório: cartão múltiplo precisa de uma conta para a função débito.'}
                </p>
              </div>
            )}
            {linkedAccounts.length === 0 && (
              <p className="text-xs text-gray-400 mt-1">Cadastre contas bancárias em <strong>Contas</strong> para vinculá-las aqui.</p>
            )}
          </div>

          {/* Forma de pagamento da fatura — somente para crédito/múltiplo */}
          {form.card_type !== 'debit' && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Forma de pagamento da fatura</label>
              <div className="space-y-2">
                {PAYMENT_METHODS.map(pm => (
                  <button
                    key={pm.value}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, payment_method: pm.value }))}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all cursor-pointer ${
                      form.payment_method === pm.value
                        ? 'border-emerald-400 bg-emerald-50'
                        : 'border-gray-150 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      form.payment_method === pm.value ? 'bg-emerald-100' : 'bg-gray-100'
                    }`}>
                      <i className={`${pm.icon} text-sm ${form.payment_method === pm.value ? 'text-emerald-600' : 'text-gray-500'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-600 ${form.payment_method === pm.value ? 'text-emerald-700' : 'text-gray-800'}`}>{pm.label}</p>
                      <p className="text-xs text-gray-400">{pm.description}</p>
                    </div>
                    {form.payment_method === pm.value && (
                      <i className="ri-check-circle-fill text-emerald-500 text-base flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              {form.payment_method === 'automatic_debit' && !form.linked_account_id && (
                <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl">
                  <i className="ri-alert-line text-amber-500 text-sm" />
                  <p className="text-xs text-amber-700">Para débito automático, vincule uma conta acima.</p>
                </div>
              )}
              {form.payment_method === 'automatic_debit' && form.linked_account_id && (
                <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <i className="ri-loop-right-line text-emerald-500 text-sm" />
                  <p className="text-xs text-emerald-700">A fatura será debitada automaticamente de <strong>{linkedAccount?.name ?? 'conta vinculada'}</strong> no vencimento.</p>
                </div>
              )}
            </div>
          )}

          {/* Cor */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Cor do cartão</label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLORS.map(c => (
                <button
                  key={c} type="button" onClick={() => setForm(p => ({ ...p, color: c }))}
                  style={{ backgroundColor: c }}
                  className={`w-7 h-7 rounded-full cursor-pointer transition-transform ${form.color === c ? 'scale-125 ring-2 ring-offset-2 ring-gray-400' : ''}`}
                />
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-xl">
            <div>
              <p className="text-sm font-600 text-gray-700">Cartão ativo</p>
              <p className="text-xs text-gray-400">Cartões inativos não comprometem limite</p>
            </div>
            <button
              type="button"
              onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
              className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer overflow-hidden ${form.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${form.is_active ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Observação */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              Observação <span className="normal-case font-400 text-gray-400">(opcional)</span>
            </label>
            <textarea
              rows={2} maxLength={300} value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Notas sobre este cartão..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 resize-none"
            />
          </div>

          {errorMsg && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
              <i className="ri-error-warning-line" />{errorMsg}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap"
            >
              {saving ? 'Salvando...' : card ? 'Salvar alterações' : 'Criar cartão'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
